import styled from 'styled-components'

import { theme } from 'styles/theme'

import { fluidType } from '../fluid-type'

const { colors, lineHeights, media, space } = theme

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${colors['neutral-background-default']};
`

export const Hero = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: ${space['spacing-xl']} 0 0;
  background-color: ${colors['neutral-background-contrast']};

  /* O pé reserva o espaço que os cards ocupam ao subir: sem ele a
     sobreposição cobre o último parágrafo quando o título quebra em mais
     linhas do que o desenho previa. */
  @media (${media.desktop}) {
    display: flex;
    align-items: center;
    min-height: min(39.125rem, 32.86vw);
    padding: ${space['spacing-xxl']} 0 8.75rem;
  }
`

export const Infos = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xl']};

  h1,
  h1 span {
    margin: 0;

    /* 64px no desenho — aqui o hero usa o mesmo passo dos títulos de seção. */
    font-size: ${fluidType.sectionTitle};
    line-height: ${lineHeights['font-line-height-sm']};
    letter-spacing: -0.023em;
  }

  h1 span br {
    display: none;
  }

  @media (max-width: 489px) {
    h1 span br {
      display: block;
    }
  }

  @media (${media.desktop}) {
    gap: 3.125rem;
  }
`

export const Paragraphs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-sm']};

  p {
    margin: 0;
    font-size: 1rem;
    line-height: ${lineHeights['font-line-height-sm']};
  }
`

export const ImageWrapper = styled.div`
  position: relative;
  z-index: 0;
  width: 100%;
  height: 240px;
  margin-top: ${space['spacing-md']};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, ${colors['neutral-background-contrast']} 0%, rgb(0 40 75 / 0%) 25%);
  }

  @media (${media.phone}) {
    height: calc(100vw * 1156 / 2238 + 200px);
    margin-top: -80px;

    img {
      object-position: 55% center;
      transform: scale(1.45) translateY(40px);
    }

    &::after {
      background: linear-gradient(to bottom, ${colors['neutral-background-contrast']} 0%, rgb(0 40 75 / 0%) 25%);
    }
  }

  @media (${media.tablet}) {
    height: 340px;
    margin-top: ${space['spacing-sm']};

    img {
      object-position: center top;
    }
  }

  @media (${media.desktop}) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 58.7%;
    height: auto;
    margin-top: 0;

    &::after {
      background: linear-gradient(to right, ${colors['neutral-background-contrast']} 0%, rgb(0 40 75 / 0%) 48%);
    }

    img {
      width: 108%;
      margin-left: -8%;
      object-position: 50% 0;
    }
  }
`

export const LineWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 82%;
  height: calc(3.5rem + 3.3 * ${fluidType.sectionTitle});

  @media (min-width: 490px) {
    height: calc(1.5rem + 3.3 * ${fluidType.sectionTitle});
  }

  img {
    display: none;
  }

  @media (${media.tablet}) {
    height: calc(3.5rem + 2.2 * ${fluidType.sectionTitle});
  }

  @media (${media.desktop}) {
    top: -5.9%;
    left: 73.7%;
    width: 26.3%;
    height: auto;

    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }
`

export const MobileLine = styled.div`
  width: 100%;
  height: 100%;
  border: 3px solid ${colors['cyan-background-strong-default']};
  border-top: 0;
  border-left: 0;

  /* Mesmo raio da curva do SVG usado no desktop. */
  border-bottom-right-radius: 100px;

  @media (${media.desktop}) {
    display: none;
  }
`

export const PolygonWrapper = styled.div`
  position: absolute;
  top: 37%;
  left: 100%;
  z-index: 2;
  transform: translate(-50%, -50%);

  @media (${media.desktop}) {
    top: calc(98.5% - 28px);
    left: 56.7%;
    transform: none;
  }
`

export const Cards = styled.div`
  position: relative;
  z-index: 4;
  width: 100%;
  padding-bottom: ${space['spacing-xxxl']};
  margin-top: -5rem;

  @media (${media.phone}) {
    padding-bottom: 0;
  }

  @media (${media.desktop}) {
    margin-top: max(-7.5rem, -6.3vw);
    padding-bottom: 0;
  }
`

export const CardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space['spacing-md']};

  @media (${media.desktop}) {
    grid-template-columns: minmax(0, 2.18fr) minmax(0, 1fr);
    align-items: stretch;
    gap: 0.625rem;
  }
`

export const PillarsCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xl']};
  padding: ${space['spacing-lg']} ${space['spacing-md']};
  overflow: hidden;
  background-color: ${colors['blue-background-strong-default']};
  border-radius: 1.875rem;

  p {
    margin: 0;
    font-size: ${fluidType.label};
    line-height: ${lineHeights['font-line-height-lg']};
  }

  @media (${media.desktop}) {
    gap: 1.875rem;
    padding: 1.875rem 3.125rem;
  }
`

export const PillarsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 1.25rem;

  @media (${media.phone}) {
    flex-direction: column;
    gap: ${space['spacing-md']};
  }
`

export const Pillar = styled.div`
  display: flex;
  align-items: center;

  p {
    font-size: ${fluidType.pillar};
    line-height: ${lineHeights['font-line-height-sm']};
    letter-spacing: -0.02em;
    white-space: nowrap;
  }

  &:not(:last-child)::after {
    content: '';
    align-self: stretch;
    width: 1px;
    margin-left: 1.25rem;
    background-color: ${colors['neutral-text-contrast-default']};
  }

  @media (${media.phone}) {
    gap: ${space['spacing-sm']};

    &::before {
      content: '•';
      color: ${colors['cyan-background-pressed']};
    }

    &:not(:last-child)::after {
      content: none;
    }
  }
`

export const QuoteCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xxs']};
  padding: ${space['spacing-lg']} ${space['spacing-md']};
  background-color: ${colors['neutral-background-default']};
  border: 1px solid ${colors['neutral-background-strong']};
  border-radius: 1.875rem;

  p {
    margin: 0;
  }

  @media (${media.desktop}) {
    justify-content: center;
    padding: 1.875rem 3.125rem;
  }
`

export const QuoteMark = styled.div<{ $align?: 'start' | 'end' }>`
  display: flex;
  justify-content: ${({ $align }) => ($align === 'end' ? 'flex-end' : 'flex-start')};

  img {
    width: 3.375rem;
    height: auto;
  }
`
